/**
 * MODULE: domain/sales/frontend/api/client.ts
 * PURPOSE: API client for account hierarchy, contacts, and unified Sales Intelligence
 * DEPENDENCIES: axios, @shared/lib/frontend/api/config, sales.types
 * SIDE_EFFECTS: HTTP requests, 401 redirect to /login
 * Recent: 2026-02-11 - Token via getAuthToken() only; X-Tenant-ID from tenant_id or tenantId. 2026-02-06 - list, promoteFromSalesIntelligence.
 */

import axios, { AxiosInstance } from 'axios';
import { API_CONFIG, getAuthToken } from '../../../../_shared/lib/frontend/api/config';
import type {
  Account,
  AccountHierarchy,
  AccountListResponse,
  Contact,
  ContactListResponse,
  CreateContactInput,
  SalesIntelligenceCompany,
  SubsidiariesResponse,
  UpdateAccountValuePyramidInput,
  UpdateContactInput,
} from '../../contracts/sales.types';

const baseURL =
  API_CONFIG.baseURL ? `${API_CONFIG.baseURL}/api/v1/sales-accounts` : '/api/v1/sales-accounts';

const client: AxiosInstance = axios.create({
  baseURL,
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' },
});

client.interceptors.request.use((config) => {
  const token = getAuthToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;

  const tenantId =
    typeof window !== 'undefined' ? localStorage.getItem('tenant_id') || localStorage.getItem('tenantId') : null;
  if (tenantId) config.headers['X-Tenant-ID'] = tenantId;

  return config;
});

client.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      if (typeof window !== 'undefined') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

export const salesAccountsApi = {
  accounts: {
    list: async (params?: {
      territory_id?: string;
      status?: string;
      search?: string;
      page?: number;
      per_page?: number;
      include_sales_intelligence?: boolean;
      source_filter?: 'crm' | 'sales_intelligence' | 'all';
    }): Promise<AccountListResponse> => {
      const res = await client.get<AccountListResponse>('/accounts', { params });
      return res.data;
    },
    promoteFromSalesIntelligence: async (
      companyId: string,
      parentAccountId?: string
    ): Promise<Account> => {
      const res = await client.post<Account>(
        '/accounts/promote-from-sales-intelligence',
        { company_id: companyId, parent_account_id: parentAccountId ?? null }
      );
      return res.data;
    },
    listSalesIntelligenceCompanies: async (params?: {
      page?: number;
      per_page?: number;
      search?: string;
      not_promoted?: boolean;
    }): Promise<{
      companies: SalesIntelligenceCompany[];
      total: number;
      page: number;
      per_page: number;
    }> => {
      const res = await client.get('/accounts/sales-intelligence/companies', {
        params,
      });
      return res.data;
    },
    create: async (data: { name: string; status?: string; territory_id?: string; parent_account_id?: string }): Promise<Account> => {
      const res = await client.post<Account>('/accounts', data);
      return res.data;
    },
    get: async (accountId: string): Promise<Account> => {
      const res = await client.get<Account>(`/accounts/${accountId}`);
      return res.data;
    },
    updateValuePyramid: async (
      accountId: string,
      data: UpdateAccountValuePyramidInput
    ): Promise<Account> => {
      const res = await client.patch<Account>(
        `/accounts/${accountId}/value-pyramid`,
        data
      );
      return res.data;
    },
  },
  hierarchy: {
    getSubsidiaries: async (accountId: string, page = 1, perPage = 20): Promise<SubsidiariesResponse> => {
      const res = await client.get<SubsidiariesResponse>(`/accounts/${accountId}/subsidiaries`, {
        params: { page, per_page: perPage },
      });
      return res.data;
    },
    getHierarchy: async (accountId: string): Promise<AccountHierarchy> => {
      const res = await client.get<AccountHierarchy>(`/accounts/${accountId}/hierarchy`);
      return res.data;
    },
    linkSubsidiary: async (parentId: string, subsidiaryId: string): Promise<Account> => {
      const res = await client.post<Account>(`/accounts/${parentId}/subsidiaries`, {
        subsidiary_id: subsidiaryId,
      });
      return res.data;
    },
    unlinkSubsidiary: async (parentId: string, subsidiaryId: string): Promise<Account> => {
      const res = await client.delete<Account>(
        `/accounts/${parentId}/subsidiaries/${subsidiaryId}`
      );
      return res.data;
    },
    updateParent: async (accountId: string, parentAccountId: string | null): Promise<Account> => {
      const res = await client.patch<Account>(`/accounts/${accountId}/parent`, {
        parent_account_id: parentAccountId,
      });
      return res.data;
    },
  },
  contacts: {
    list: async (
      accountId: string,
      page = 1,
      perPage = 20,
      search?: string
    ): Promise<ContactListResponse> => {
      const res = await client.get<ContactListResponse>(`/accounts/${accountId}/contacts`, {
        params: { page, per_page: perPage, search },
      });
      return res.data;
    },
    create: async (accountId: string, data: CreateContactInput): Promise<Contact> => {
      const res = await client.post<Contact>(`/accounts/${accountId}/contacts`, data);
      return res.data;
    },
    get: async (contactId: string): Promise<Contact> => {
      const res = await client.get<Contact>(`/contacts/${contactId}`);
      return res.data;
    },
    update: async (contactId: string, data: UpdateContactInput): Promise<Contact> => {
      const res = await client.patch<Contact>(`/contacts/${contactId}`, data);
      return res.data;
    },
    delete: async (contactId: string): Promise<void> => {
      await client.delete(`/contacts/${contactId}`);
    },
    setPrimary: async (contactId: string): Promise<Contact> => {
      const res = await client.patch<Contact>(`/contacts/${contactId}/set-primary`);
      return res.data;
    },
  },
};
